import type { ViewStateChangeParameters } from "@deck.gl/core";
import type { GeoJSON } from "geojson";
import React from "react";

import type { ViewportType } from "~/lib/map/types";
import { getCenterCoordsFromGeoJson, getCenterCoordsFromH3Indexes } from "~/lib/map/utils";

const INITIAL_VIEW_STATE = {
  latitude: 35.3607411,
  longitude: 138.727262,
  zoom: 4,
  pitch: 0,
  bearing: 0,
  minZoom: 3,
  maxZoom: 10,
} as const;

type useMapCommonProps = {
  width: number;
  height: number;
  areaH3Indexes: string[];
  geometry: GeoJSON | null;
  initialViewState?: Partial<ViewportType>;
};
export const useMapBase = ({ width, height, areaH3Indexes, geometry, initialViewState }: useMapCommonProps) => {
  const initialCenterCoords = React.useMemo(() => {
    if (areaH3Indexes.length > 0) {
      return getCenterCoordsFromH3Indexes(areaH3Indexes);
    }

    if (geometry) {
      return getCenterCoordsFromGeoJson(geometry);
    }

    return undefined;
  }, [areaH3Indexes, geometry]);

  const computedInitialViewState = React.useMemo(
    () => ({
      ...INITIAL_VIEW_STATE,
      width,
      height,
      ...(initialCenterCoords
        ? {
            latitude: initialCenterCoords.lat,
            longitude: initialCenterCoords.lng,
          }
        : undefined),
      ...(initialViewState || {}),
    }),
    [width, height, initialCenterCoords, initialViewState],
  );

  const [viewState, setViewState] = React.useState<ViewportType>(computedInitialViewState);

  // `useState` only reads its argument once, so a container that is measured after mount or
  // later resized would leave `viewState` pinned to the old size. Layer extents are unprojected
  // from it (`getBoundsFromViewport`), so they would stay wrong until the next pan or zoom.
  React.useEffect(() => {
    setViewState((prev: ViewportType) =>
      prev.width === width && prev.height === height ? prev : { ...prev, width, height },
    );
  }, [width, height]);

  const onViewStateChange = React.useCallback((param: ViewStateChangeParameters) => {
    // don't update viewState when in transition or dragging
    if (!param.interactionState.inTransition && !param.interactionState.isDragging) {
      setViewState(param.viewState as ViewportType);
    }
  }, []);

  return { viewState, onViewStateChange, setViewState };
};
